import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthPaidMediaTopic,
  liveStatusTranslationKey
} from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import {
  GROWTH_PAID_MEDIA_NATIONS,
  GROWTH_PAID_MEDIA_PROVIDERS,
  GrowthPaidMediaAccountDto
} from './growth-paid-media.models';
import { GrowthPaidMediaService } from './growth-paid-media.service';

@Component({
  standalone: true,
  selector: 'ain-growth-paid-media-accounts',
  imports: [
    AinPageComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    TableModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    DialogModule,
    RouterLink
  ],
  template: `
    <ain-page [title]="t('growth.paidMedia.accounts.title')" [description]="t('growth.paidMedia.accounts.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.paidMedia.accounts.help.title'"
        [descriptionKey]="'growth.paidMedia.accounts.help.description'"
        [bulletKeys]="['growth.paidMedia.accounts.help.bullets.residency', 'growth.paidMedia.accounts.help.bullets.live']" />

      <section class="card list-card">
        <div class="toolbar">
          <input pInputText type="text" [(ngModel)]="searchTerm" [placeholder]="t('growth.paidMedia.accounts.search')" />
          <button pButton type="button" [label]="t('growth.paidMedia.accounts.search')" (click)="applyFilters()"></button>
          <button pButton type="button" [label]="t('growth.paidMedia.accounts.refresh')" severity="secondary" (click)="load()"></button>
          <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
        </div>
        <div class="toolbar">
          <button
            pButton
            type="button"
            [label]="t('growth.paidMedia.accounts.actions.openSelected')"
            severity="secondary"
            [disabled]="!selected()"
            (click)="openSelectedDetail()"></button>
          @if (canDisableSelected()) {
            <button
              pButton
              type="button"
              severity="danger"
              [label]="t('growth.paidMedia.accounts.actions.disable')"
              [loading]="actionType() === 'disable'"
              (click)="disableSelected()"></button>
          }
          @if (canEnableKillSwitchSelected()) {
            <button
              pButton
              type="button"
              severity="warn"
              [label]="t('growth.paidMedia.accounts.actions.enableKillSwitch')"
              (click)="openKillSwitchDialog(true)"></button>
          }
          @if (canClearKillSwitchSelected()) {
            <button
              pButton
              type="button"
              [label]="t('growth.paidMedia.accounts.actions.clearKillSwitch')"
              [loading]="actionType() === 'killSwitch'"
              (click)="clearKillSwitchSelected()"></button>
          }
          <button pButton type="button" [label]="t('growth.paidMedia.accounts.actions.new')" (click)="openCreateDialog()"></button>
          <a class="link-btn" routerLink="/growth/paid-media/campaign-links">{{ t('growth.paidMedia.accounts.openCampaignLinks') }}</a>
        </div>
        <p-table
          [value]="filteredRows()"
          selectionMode="single"
          [selection]="selected()"
          (selectionChange)="onSelect($event)"
          dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('growth.paidMedia.accounts.columns.providerKey') }}</th>
              <th>{{ t('growth.paidMedia.accounts.columns.connectorKey') }}</th>
              <th>{{ t('growth.paidMedia.accounts.columns.nationCode') }}</th>
              <th>{{ t('growth.paidMedia.accounts.columns.isEnabled') }}</th>
              <th>{{ t('growth.paidMedia.accounts.columns.canBeEnabled') }}</th>
              <th>{{ t('growth.paidMedia.accounts.columns.killSwitch') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [pSelectableRow]="row">
              <td>
                <button type="button" class="link-btn" (click)="openDetail(row.id)">{{ providerLabel(row.providerKey) }}</button>
              </td>
              <td>{{ row.connectorKey }}</td>
              <td>{{ row.nationCode }}</td>
              <td>{{ row.isEnabled ? t('common.yes') : t('common.no') }}</td>
              <td>{{ row.canBeEnabled ? t('common.yes') : t('common.no') }}</td>
              <td>{{ row.killSwitchEnabled ? t('common.yes') : t('common.no') }}</td>
            </tr>
          </ng-template>
        </p-table>
        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
        @if (successMessage()) {
          <p class="message success">{{ successMessage() }}</p>
        }
      </section>
    </ain-page>

    <p-dialog
      [header]="t('growth.paidMedia.accounts.createDialog.title')"
      [visible]="createDialogVisible()"
      (visibleChange)="createDialogVisible.set($event)"
      [modal]="true">
      <ain-info-hint
        [titleKey]="'growth.paidMedia.accounts.createDialog.help.title'"
        [descriptionKey]="'growth.paidMedia.accounts.createDialog.help.description'" />
      <div class="dialog-grid">
        <ain-form-field [label]="t('growth.paidMedia.accounts.fields.providerKey')">
          <p-select
            [options]="providerOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="createModel.providerKey"
            appendTo="body" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.paidMedia.accounts.fields.nationCode')">
          <p-select
            [options]="nationOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="createModel.nationCode"
            appendTo="body" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.paidMedia.accounts.fields.authConfigRef')" class="full">
          <input pInputText type="text" [(ngModel)]="createModel.authConfigRef" [placeholder]="t('growth.paidMedia.accounts.fields.authConfigRefPlaceholder')" />
        </ain-form-field>
      </div>
      <ng-template pTemplate="footer">
        <button pButton type="button" [label]="t('common.cancel')" severity="secondary" (click)="createDialogVisible.set(false)"></button>
        <button pButton type="button" [label]="t('common.save')" [loading]="actionType() === 'create'" (click)="saveCreate()"></button>
      </ng-template>
    </p-dialog>

    <p-dialog
      [header]="t('growth.paidMedia.accounts.killSwitchDialog.title')"
      [visible]="killSwitchDialogVisible()"
      (visibleChange)="killSwitchDialogVisible.set($event)"
      [modal]="true">
      <ain-info-hint
        [titleKey]="'growth.paidMedia.accounts.killSwitchDialog.help.title'"
        [descriptionKey]="'growth.paidMedia.accounts.killSwitchDialog.help.description'" />
      <ain-form-field [label]="t('growth.paidMedia.accounts.fields.killSwitchReason')">
        <input pInputText type="text" [(ngModel)]="killSwitchReason" />
      </ain-form-field>
      <ng-template pTemplate="footer">
        <button pButton type="button" [label]="t('common.cancel')" severity="secondary" (click)="killSwitchDialogVisible.set(false)"></button>
        <button
          pButton
          type="button"
          severity="warn"
          [label]="t('growth.paidMedia.accounts.actions.enableKillSwitch')"
          [loading]="actionType() === 'killSwitch'"
          (click)="confirmKillSwitch()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; }
    .list-card { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; text-decoration: none; }
    .link-btn:hover { text-decoration: underline; }
    .message { margin: 0; font-size: 13px; }
    .message.error { color: #b91c1c; }
    .message.success { color: #15803d; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 10px; }
    .dialog-grid .full { grid-column: 1 / -1; }
    @media (max-width: 640px) { .dialog-grid { grid-template-columns: 1fr; } }
  `]
})
export class GrowthPaidMediaAccountsComponent implements OnInit {
  private readonly api = inject(GrowthPaidMediaService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly rows = signal<GrowthPaidMediaAccountDto[]>([]);
  readonly filteredRows = signal<GrowthPaidMediaAccountDto[]>([]);
  readonly selected = signal<GrowthPaidMediaAccountDto | null>(null);
  readonly errorMessage = signal('');
  readonly successMessage = signal('');
  readonly createDialogVisible = signal(false);
  readonly killSwitchDialogVisible = signal(false);
  readonly actionType = signal<'create' | 'disable' | 'killSwitch' | null>(null);

  searchTerm = '';
  killSwitchReason = '';
  createModel = {
    providerKey: 'yektanet',
    nationCode: 'IR',
    authConfigRef: ''
  };

  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthPaidMediaChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load(false));
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.load(true);
  }

  providerOptions(): Array<{ label: string; value: string }> {
    return GROWTH_PAID_MEDIA_PROVIDERS.map(value => ({ value, label: this.providerLabel(value) }));
  }

  nationOptions(): Array<{ label: string; value: string }> {
    return GROWTH_PAID_MEDIA_NATIONS.map(value => ({ value, label: value }));
  }

  providerLabel(providerKey: string): string {
    return this.t(`growth.paidMedia.providers.${providerKey}`);
  }

  canDisableSelected(): boolean {
    const row = this.selected();
    return !!row?.isEnabled;
  }

  canEnableKillSwitchSelected(): boolean {
    const row = this.selected();
    return !!row && !row.killSwitchEnabled;
  }

  canClearKillSwitchSelected(): boolean {
    return !!this.selected()?.killSwitchEnabled;
  }

  load(showError = true): void {
    if (showError) {
      this.errorMessage.set('');
    }
    this.api.listAccounts().subscribe({
      next: data => {
        this.rows.set(data);
        this.applyFilters();
      },
      error: () => {
        this.rows.set([]);
        this.filteredRows.set([]);
        this.selected.set(null);
        this.errorMessage.set(this.t('growth.paidMedia.accounts.messages.loadFailed'));
      }
    });
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();
    let filtered = this.rows();
    if (term) {
      filtered = filtered.filter(item =>
        item.providerKey.toLowerCase().includes(term)
        || item.connectorKey.toLowerCase().includes(term)
        || item.nationCode.toLowerCase().includes(term));
    }
    this.filteredRows.set(filtered);
    const currentId = this.selected()?.id;
    this.selected.set(filtered.find(item => item.id === currentId) ?? filtered[0] ?? null);
  }

  onSelect(row: GrowthPaidMediaAccountDto | null): void {
    this.selected.set(row);
  }

  openSelectedDetail(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.openDetail(row.id);
  }

  openDetail(accountId: string): void {
    void this.router.navigate(['/growth/paid-media/accounts', accountId]);
  }

  openCreateDialog(): void {
    this.createModel = { providerKey: 'yektanet', nationCode: 'IR', authConfigRef: '' };
    this.createDialogVisible.set(true);
  }

  saveCreate(): void {
    this.actionType.set('create');
    this.errorMessage.set('');
    this.successMessage.set('');
    this.api.createAccount({
      providerKey: this.createModel.providerKey,
      nationCode: this.createModel.nationCode,
      authConfigRef: this.createModel.authConfigRef.trim() || null
    }).pipe(finalize(() => this.actionType.set(null))).subscribe({
      next: created => {
        this.createDialogVisible.set(false);
        this.successMessage.set(this.t('growth.paidMedia.accounts.messages.createSucceeded'));
        this.load(false);
        void this.router.navigate(['/growth/paid-media/accounts', created.id]);
      },
      error: () => this.errorMessage.set(this.t('growth.paidMedia.accounts.messages.createFailed'))
    });
  }

  disableSelected(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.actionType.set('disable');
    this.errorMessage.set('');
    this.successMessage.set('');
    this.api.disableAccount(row.id).pipe(finalize(() => this.actionType.set(null))).subscribe({
      next: () => {
        this.successMessage.set(this.t('growth.paidMedia.accounts.messages.disableSucceeded'));
        this.load(false);
      },
      error: () => this.errorMessage.set(this.t('growth.paidMedia.accounts.messages.disableFailed'))
    });
  }

  openKillSwitchDialog(enabled: boolean): void {
    if (!enabled || !this.selected()) {
      return;
    }
    this.killSwitchReason = '';
    this.killSwitchDialogVisible.set(true);
  }

  confirmKillSwitch(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.actionType.set('killSwitch');
    this.errorMessage.set('');
    this.successMessage.set('');
    this.api.setKillSwitch(row.id, {
      enabled: true,
      reason: this.killSwitchReason.trim() || null
    }).pipe(finalize(() => this.actionType.set(null))).subscribe({
      next: () => {
        this.killSwitchDialogVisible.set(false);
        this.successMessage.set(this.t('growth.paidMedia.accounts.messages.killSwitchSucceeded'));
        this.load(false);
      },
      error: () => this.errorMessage.set(this.t('growth.paidMedia.accounts.messages.killSwitchFailed'))
    });
  }

  clearKillSwitchSelected(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.actionType.set('killSwitch');
    this.errorMessage.set('');
    this.successMessage.set('');
    this.api.setKillSwitch(row.id, { enabled: false }).pipe(finalize(() => this.actionType.set(null))).subscribe({
      next: () => {
        this.successMessage.set(this.t('growth.paidMedia.accounts.messages.killSwitchCleared'));
        this.load(false);
      },
      error: () => this.errorMessage.set(this.t('growth.paidMedia.accounts.messages.killSwitchFailed'))
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthPaidMediaTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
